import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../api.js";
import { pluralize } from "../ui.js";
import {
  formatDuration,
  formatMinor,
  formatStartedAt,
  outcomeLabel,
  type CallWithOrder
} from "../calls-model.js";
import { NO_RECORDING_HINT, recordingView } from "../recording-model.js";
import {
  BagIcon,
  CalendarIcon,
  CallOutcomeIcon,
  CloseIcon,
  Hint,
  CoinIcon,
  RecordIcon,
  TimerIcon,
  UserIcon,
  WithIcon
} from "../icons.js";
import { Modal } from "../modal.js";

/** "1m 23s" into the call — relative offsets read better than clock times when following one. */
function offset(atMs: number): string {
  const total = Math.max(0, Math.floor(atMs / 1000));
  const m = Math.floor(total / 60);
  return m > 0 ? `${m}:${String(total % 60).padStart(2, "0")}` : `0:${String(total).padStart(2, "0")}`;
}

const ROLE_LABELS: Record<string, string> = {
  agent: "Agent",
  caller: "Caller",
  tool: "Action",
  system: "Event"
};

/**
 * What was said on one call, with the agent's tool calls interleaved.
 *
 * Tool calls are shown, not hidden: seeing "add_items {...}" between two spoken turns is what
 * makes a wrong order explicable. They're visually de-emphasised so the conversation still
 * reads as a conversation.
 */
export function TranscriptDialog({
  restaurantId,
  call,
  onClose
}: {
  restaurantId: string;
  call: CallWithOrder;
  onClose: () => void;
}) {
  const transcript = useQuery({
    queryKey: ["transcript", restaurantId, call.callSid],
    queryFn: () => api.getTranscript(restaurantId, call.callSid),
    retry: false // an expired transcript is expected, not a failure worth retrying
  });

  const expired =
    transcript.error instanceof ApiError && transcript.error.status === 404;

  return (
    <Modal label="Call" wide onClose={onClose}>
        <div className="modal-head">
          <h2>Call</h2>
          <button className="ghost icon-button" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <p className="muted small facts-line">
          <WithIcon icon={<CalendarIcon size={15} />}>{formatStartedAt(call.startedAtIso)}</WithIcon>
          <WithIcon icon={<TimerIcon size={15} />}>{formatDuration(call.durationSeconds)}</WithIcon>
          <WithIcon icon={<CallOutcomeIcon outcome={call.outcome} size={15} />}>{outcomeLabel(call.outcome)}</WithIcon>
          <WithIcon icon={<CoinIcon size={15} />}>{formatMinor(call.billedMinor)}</WithIcon>
          {call.fromNumber ? <WithIcon icon={<UserIcon size={15} />}>{call.fromNumber}</WithIcon> : null}
          {call.order ? (
            <WithIcon icon={<BagIcon size={15} />}>{formatMinor(call.order.total.amountMinor)}</WithIcon>
          ) : null}
        </p>

        <RecordingPlayer restaurantId={restaurantId} callSid={call.callSid} />

        {/* What was ordered, beside what was said: the two halves of the same question. Read
            from the ORDER itself, which is the source of truth for its contents. */}
        {call.order && call.order.lines.length > 0 ? (
          <details className="order-detail-block" open>
            <summary>
              <BagIcon size={15} /> Order · {formatMinor(call.order.total.amountMinor)} ·{" "}
              {pluralize(call.order.lines.reduce((n, l) => n + l.quantity, 0), "item")}
            </summary>
            <ul className="order-lines">
              {call.order.lines.map((l, i) => (
                <li key={`${l.nameSnapshot}-${i}`}>
                  <strong>{l.quantity}×</strong> {l.nameSnapshot}
                  {l.modifiers.length > 0 ? (
                    <span className="muted"> — {l.modifiers.join(", ")}</span>
                  ) : null}
                  <span className="muted"> {formatMinor(l.unitPrice.amountMinor)}</span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}

        {transcript.isLoading ? <p className="muted">Loading transcript…</p> : null}

        {expired ? (
          <p className="muted small">
            No transcript <Hint>Transcripts are kept for 30 days.</Hint>
          </p>
        ) : transcript.error ? (
          <div className="notice error">
            {transcript.error instanceof ApiError
              ? transcript.error.message
              : String(transcript.error)}
          </div>
        ) : null}

        {transcript.data ? (
          transcript.data.entries.length === 0 ? (
            <p className="muted">Nothing was recorded for this call.</p>
          ) : (
            <>
              <ol className="transcript">
                {transcript.data.entries.map((e, i) => (
                  <li key={`${e.atMs}-${i}`} className={`turn role-${e.role}`}>
                    <span className="turn-at">{offset(e.atMs)}</span>
                    <span className="turn-role">{ROLE_LABELS[e.role] ?? e.role}</span>
                    <span className="turn-text">{e.text}</span>
                  </li>
                ))}
              </ol>
              {transcript.data.truncated ? (
                <p className="muted small">Long call: only the first part was kept.</p>
              ) : null}
              {/* Caller turns need input transcription, which isn't enabled; without saying
                  so, a one-sided transcript looks like a bug. */}
              {transcript.data.entries.every((e) => e.role !== "caller") ? (
                <p className="muted small">
                  Agent side only <Hint>Caller speech isn’t transcribed yet.</Hint>
                </p>
              ) : null}
            </>
          )
        ) : null}

    </Modal>
  );
}

/**
 * The call's audio, played straight from storage with a short-lived link.
 *
 * The link lasts 15 minutes. If the player fails, most likely because the dialog sat open past that, one new
 * link is fetched. Only one: a second failure is a real problem and is shown rather than retried in a loop.
 */
function RecordingPlayer({ restaurantId, callSid }: { restaurantId: string; callSid: string }) {
  const link = useQuery({
    queryKey: ["recording", restaurantId, callSid],
    queryFn: () => api.getRecording(restaurantId, callSid),
    retry: false,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false
  });
  const [refreshed, setRefreshed] = useState(false);
  const [playerFailed, setPlayerFailed] = useState(false);

  const view = recordingView({
    loading: link.isLoading,
    ...(link.data ? { url: link.data.url } : {}),
    ...(link.error instanceof ApiError
      ? { errorStatus: link.error.status, errorMessage: link.error.message }
      : link.error
        ? { errorMessage: String(link.error) }
        : {})
  });

  return (
    <div className="recording">
      <h3>
        <WithIcon icon={<RecordIcon size={16} />}>Recording</WithIcon>
      </h3>
      {view.kind === "loading" ? <p className="muted small">Loading recording…</p> : null}
      {view.kind === "none" ? (
        <p className="muted small">
          No recording <Hint>{NO_RECORDING_HINT}</Hint>
        </p>
      ) : null}
      {view.kind === "error" ? <div className="notice error">{view.message}</div> : null}
      {view.kind === "ready" ? (
        <>
          <audio
            controls
            preload="metadata"
            src={view.url}
            onError={() => {
              if (!refreshed) {
                setRefreshed(true);
                void link.refetch();
              } else {
                setPlayerFailed(true);
              }
            }}
          />
          {playerFailed ? (
            <div className="notice error">The recording could not be played. Close and reopen the call to try again.</div>
          ) : (
            <p className="muted small">Left: caller · Right: agent</p>
          )}
        </>
      ) : null}
    </div>
  );
}
